import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'

import { signOut } from '../auth/AuthGate.jsx'
import {
  computeFailedPayments,
  computeOverdueInvoices,
  watchCustomers,
  watchInvoices,
} from '../lib/data.js'
import logo from '../assets/yesnet-logo.png'

const NAV = [
  { to: '/', label: 'Dashboard', icon: '📊', end: true },
  // Four strictly-partitioned customer tabs. Every customer falls into
  // exactly one based on (customerType, billingCycle). AL and YN never
  // mix; 1st and 15th never mix.
  //   program + 1st   → YN AL Customers 1st
  //   program + 15th  → YN AL Customers 15th
  //   invoice + 1st   → YN Customers 1st
  //   invoice + 15th  → YN Customers 15th
  { to: '/customers/al-1st',  label: 'YN AL Customers 1st',  icon: '👥' },
  { to: '/customers/al-15th', label: 'YN AL Customers 15th', icon: '👥' },
  { to: '/customers/yn-1st',  label: 'YN Customers 1st',     icon: '🧮' },
  { to: '/customers/yn-15th', label: 'YN Customers 15th',    icon: '🧮' },
  { to: '/customers/yn-custom', label: 'YN Customers Custom', icon: '🧮' },
  { to: '/invoices', label: 'Invoices', icon: '🧾' },
  { to: '/failed-payments', label: 'Failed Payments', icon: '❌', badgeKey: 'failed' },
  { to: '/overdue', label: 'Overdue', icon: '⏰', badgeKey: 'overdue' },
  { to: '/billing', label: 'Billing', icon: '💳' },
  { to: '/settings', label: 'Settings', icon: '⚙️' },
]

export default function AppShell() {
  const [customers, setCustomers] = useState({})
  const [invoices, setInvoices] = useState({})
  useEffect(() => watchCustomers(setCustomers), [])
  useEffect(() => watchInvoices(setInvoices), [])
  const badges = {
    failed: computeFailedPayments(invoices, customers).length,
    overdue: computeOverdueInvoices(invoices, customers).length,
  }

  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-60 bg-brand-green-dark text-white flex-col">
        <div className="p-4 border-b border-white/10 bg-white">
          <img src={logo} alt="YesNet" className="block w-full max-h-16 object-contain" />
        </div>
        <div className="px-5 py-2 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/60">Admin</div>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={!!n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-bold transition ${
                  isActive
                    ? 'bg-brand-gold text-brand-green'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`
              }>
              <span>{n.icon}</span>
              <span className="flex-1">{n.label}</span>
              {n.badgeKey && badges[n.badgeKey] > 0 ? (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-extrabold">
                  {badges[n.badgeKey]}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            onClick={signOut}
            className="w-full text-left text-xs uppercase tracking-widest text-white/60 hover:text-white">
            🔒 Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-brand-green-dark text-white px-3 py-2 flex items-center justify-between">
        <img src={logo} alt="YesNet" className="h-7 bg-white rounded px-2 py-0.5" />
        <button onClick={signOut} className="text-[11px] uppercase tracking-widest text-white/70">
          Sign out
        </button>
      </div>

      <main className="flex-1 overflow-x-hidden md:pt-0 pt-12">
        <div className="md:hidden border-b border-gray-200 bg-white sticky top-12 z-20 px-3 py-2 overflow-x-auto whitespace-nowrap flex gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={!!n.end}
              className={({ isActive }) =>
                `inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-bold transition ${
                  isActive
                    ? 'bg-brand-green text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }>
              <span>{n.icon}</span>
              <span>{n.label}</span>
              {n.badgeKey && badges[n.badgeKey] > 0 ? (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-extrabold">
                  {badges[n.badgeKey]}
                </span>
              ) : null}
            </NavLink>
          ))}
        </div>
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
